import { orgAPI } from "@/api/api";
import { isApiError, isZenseiApiErrorBody } from "@/api/utils";
import { userPresenceColor } from "@/lib/liveblocks/userPresenceColor";
import { queryClient } from "@/queryClient";
import { LiveblocksProvider } from "@liveblocks/react";
import type { PropsWithChildren } from "react";

/**
 * Wraps LiveblocksProvider so it can obtain access tokens from
 * `POST /api/v2/org/:organization_id/user/:user_id/liveblocks-token` (room in
 * JSON body) via the api-client.
 *
 * Auth-agnostic: this provider only depends on `orgAPI` having a bearer token
 * configured by the time `authEndpoint` is invoked. Two callers wire that up:
 *   - main app: `AuthInject` points `orgAPI` at the Auth0 token (this provider
 *     is rendered inside `Auth0ProviderWithRedirectCallback` in `App.tsx`).
 *   - canvas hero capture: `CaptureOnlyApiInject` points `orgAPI` at the
 *     short-lived capture JWT (provider is rendered without Auth0 in
 *     `canvas-capture.tsx`).
 *
 * `resolveUsers` is the canonical Liveblocks pattern for surfacing user info
 * (name / avatar / color) to default UI components like `<Cursors />`,
 * `<Thread>`, `<InboxNotification>`, etc. — they call `useUser(userId)` under
 * the hood, which dispatches into this resolver.
 *
 * We hit the same `/api/v2/org/:org/users` endpoint that powers the rest of
 * the app (members list, share dialog, etc.) and route through the global
 * `queryClient` so successive lookups share its cache. The deterministic color
 * mirrors the Go backend's `userPresenceColor`, keeping `useUser(...).color`
 * and `useOther(...).info.color` consistent.
 */
export function LiveblocksAuthProvider({ children }: PropsWithChildren) {
  // Map Zensei error envelopes to the body shape the Liveblocks browser SDK
  // expects from its `authEndpoint`:
  //   - 4xx (permanent: bad room, no permission, user gone) → return
  //     `{error, reason}` so the SDK stops its reconnect loop.
  //   - everything else (5xx, network, unexpected) → rethrow so the SDK
  //     retries with backoff.
  const authEndpoint = async (room?: string) => {
    if (!room) {
      throw new Error("Liveblocks authentication requires a room id.");
    }

    try {
      return await orgAPI.liveblocks.auth({ room });
    } catch (error) {
      if (isApiError(error) && error.status >= 400 && error.status < 500) {
        const reason = isZenseiApiErrorBody(error.data)
          ? (error.data.error?.message ?? "Access denied.")
          : "Access denied.";
        return { error: "forbidden", reason };
      }
      throw error;
    }
  };

  const resolveUsers = async ({
    userIds,
  }: {
    userIds: string[];
  }): Promise<
    ({ name: string; avatar: string; color: string } | undefined)[]
  > => {
    const users = await queryClient.fetchQuery({
      queryKey: ["users"],
      queryFn: () => orgAPI.users.list(),
    });
    const byId = new Map(users.map((u) => [u.id, u]));
    return userIds.map((id) => {
      const user = byId.get(id);
      if (!user) return undefined;
      return {
        name: user.name,
        avatar: user.picture_url,
        color: userPresenceColor(id),
      };
    });
  };

  return (
    <LiveblocksProvider
      authEndpoint={authEndpoint}
      resolveUsers={resolveUsers}
      // Rooms held open by TabPreloadProvider (and the mounted canvas) park
      // their websockets after the browser tab has been hidden this long, then
      // auto-reconnect and resync storage on focus.
      backgroundKeepAliveTimeout={15 * 60 * 1000}
    >
      {children}
    </LiveblocksProvider>
  );
}
